import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../ui/pagination';
import { Search, Edit } from 'lucide-react';
import { AppDispatch, RootState } from '../../../redux/store';
import { doGetClaims } from '../../../modules/claims/stores/thunk';
import { selectAllClaims } from '../../../modules/claims/stores/slice';
import { ClaimType } from '../../../modules/claims/types';
import moment from 'moment';
import { Skeleton } from '../../ui/skeleton';
import ClaimView from './ClaimView';
import usePageView from '../../../hooks/usePageView';
import { useParams } from 'react-router-dom';
import { formatClaimStatusText } from './utils';
import { doGetCarriers } from '../../../modules/carriers/stores/thunk';
import { selectAllCarriers } from '../../../modules/carriers/stores/slice';
import { ROLES } from '../../../constants';
import { MultiSelect } from '../../ui/select-multi';

const ClaimsPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { id } = useParams<{ id: number }>();

  usePageView(window.location.href);

  // Redux state
  const user = useSelector((state: RootState) => state.auth.user);
  const claims = useSelector(selectAllClaims);
  const isFetching = useSelector((state: any) => state.claim.isFetching);
  const metadata = useSelector((state: any) => state.claim.metadata);
  const carriers = useSelector((state: RootState) => selectAllCarriers(state));

  // Local state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [carrierFilter, setCarrierFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedClaimId, setSelectedClaimId] = useState<number | null>(null);
  const [isClaimViewOpen, setIsClaimViewOpen] = useState(false);

  // Fetch claims on component mount and when filters change
  useEffect(() => {
    const params = {
      page: currentPage,
      take: itemsPerPage,
      ...(searchTerm && { q: searchTerm }),
      ...(statusFilter && statusFilter.length > 0 && { status: statusFilter.join(',') }),
      ...(carrierFilter && carrierFilter !== 'all' && { carrierId: carrierFilter }),
      ...(sortBy && { sortBy }),
      ...(sortOrder && { sortOrder }),
    };

    dispatch(doGetClaims(params));
  }, [dispatch, currentPage, itemsPerPage, searchTerm, statusFilter, sortBy, sortOrder, carrierFilter]);

  useEffect(() => {
    if (user.role === ROLES.ADMIN) {
      dispatch(doGetCarriers({}));
      setStatusFilter(['FILED', 'UNDER_REVIEW']);
    }
    if (user.role === ROLES.CARRIER_USER) {
      setStatusFilter(['FILED']);
    }
  }, [user.role]);

  useEffect(() => {
    if (id) {
      setSelectedClaimId(id);
      setIsClaimViewOpen(true);
    }
  }, [id]);

  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle status filter change
  const handleStatusFilterChange = (value: string[]) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleCarrierFilterChange = (value: string) => {
    setCarrierFilter(value);
    setCurrentPage(1);
  };

  // Handle sort change
  const handleSortChange = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  // Get status badge variant
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'FILED':
        return 'secondary';
      case 'GRANTED':
        return 'default';
      case 'REJECTED':
        return 'destructive';
      case 'UNDER_REVIEW':
        return 'outline';
      default:
        return 'outline';
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return moment(dateString).format('MMM DD, YYYY');
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(Number(value));
    setCurrentPage(1);
  };

  // Calculate pagination info
  const totalPages = metadata?.totalPages || 1;
  const totalItems = metadata?.totalItems || 0;
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">Claims</h1>
        <p className="text-muted-foreground">Manage and track your claims</p>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search by Order Id, Reference, Reason..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </form>

            {user.role === 'ADMIN' && (
              <Select value={carrierFilter} onValueChange={handleCarrierFilterChange}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by carrier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem key={null} value="all">
                    All Carriers
                  </SelectItem>
                  {carriers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Status Filter */}

            <MultiSelect
              placeholder="Filter status..."
              value={statusFilter}
              onChange={handleStatusFilterChange}
              options={[
                { value: 'FILED', label: 'Filed' },
                { value: 'GRANTED', label: 'Granted' },
                { value: 'UNDER_REVIEW', label: 'Review' },
                { value: 'REJECTED', label: 'Rejected' },
              ]}
            />

            {/* Items per page */}
            <Select value={itemsPerPage.toString()} onValueChange={handleItemsPerPageChange}>
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 per page</SelectItem>
                <SelectItem value="25">25 per page</SelectItem>
                <SelectItem value="50">50 per page</SelectItem>
                <SelectItem value="100">100 per page</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Claims Table */}
      <Card>
        <CardHeader>
          <CardTitle>Claims</CardTitle>
          {/* <CardDescription>
            Showing {startItem} to {endItem} of {totalItems} claims
          </CardDescription> */}
        </CardHeader>
        <CardContent>
          {isFetching ? (
            <ClaimsSkeleton />
          ) : claims.length === 0 ? (
            <div className="rounded-md border w-full">
              <Table className="w-full">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[120px]">
                      <Skeleton className="h-4 w-16" />
                    </TableHead>
                    <TableHead className="w-[150px]">
                      <Skeleton className="h-4 w-24" />
                    </TableHead>
                    <TableHead className="w-[100px]">
                      <Skeleton className="h-4 w-12" />
                    </TableHead>
                    <TableHead className="w-[120px]">
                      <Skeleton className="h-4 w-20" />
                    </TableHead>
                    <TableHead className="min-w-[200px]">
                      <Skeleton className="h-4 w-16" />
                    </TableHead>
                    <TableHead className="w-[140px]">
                      <Skeleton className="h-4 w-16" />
                    </TableHead>
                    <TableHead className="text-right w-[100px]">
                      <Skeleton className="h-4 w-16 ml-auto" />
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12">
                      <p className="text-muted-foreground">No claims found</p>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          ) : (
            <>
              <div className="rounded-md border w-full">
                <Table className="w-full">
                  <TableHeader>
                    <TableRow>
                      <TableHead
                        className="cursor-pointer hover:bg-muted/50 w-[120px]"
                        onClick={() => handleSortChange('orderId')}
                      >
                        Order ID
                        {sortBy === 'orderId' && <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                      </TableHead>
                      <TableHead className="w-[150px]">Order Reference</TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-muted/50 w-[100px]"
                        onClick={() => handleSortChange('status')}
                      >
                        Status
                        {sortBy === 'status' && <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-muted/50 w-[120px]"
                        onClick={() => handleSortChange('orderValue')}
                      >
                        Order Value
                        {sortBy === 'orderValue' && <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                      </TableHead>
                      <TableHead className="min-w-[200px]">Reason</TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-muted/50 w-[140px]"
                        onClick={() => handleSortChange('createdAt')}
                      >
                        Created
                        {sortBy === 'createdAt' && <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                      </TableHead>
                      <TableHead className="text-right w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {claims.map((claim: ClaimType) => (
                      <TableRow key={claim.id}>
                        <TableCell className="font-medium">{claim.order.orderId}</TableCell>
                        <TableCell>{claim.order?.orderRef || 'N/A'}</TableCell>
                        <TableCell>
                          <Badge variant={getStatusBadgeVariant(claim.status)} className="whitespace-nowrap">
                            {formatClaimStatusText(claim.status)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-muted-foreground ">€ {claim.order?.orderValue?.toFixed(2)}</div>
                        </TableCell>
                        <TableCell>
                          <div className="truncate max-w-[200px]" title={claim.reason || ''}>
                            {claim.reason || '-'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-muted-foreground">{formatDate(claim.createdAt)}</div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedClaimId(claim.id);
                                setIsClaimViewOpen(true);
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <ClaimView claimId={selectedClaimId} open={isClaimViewOpen} onOpenChange={setIsClaimViewOpen} />
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between space-x-2 py-4">
                  <div className="text-sm text-muted-foreground">
                    Showing {startItem} to {endItem} of {totalItems} claims
                  </div>
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        {currentPage > 1 && (
                          <PaginationPrevious
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              handlePageChange(currentPage - 1);
                            }}
                            className="text-black"
                          />
                        )}
                      </PaginationItem>

                      {/* Page numbers */}
                      {Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
                        let pageNumber;
                        if (totalPages <= 5) {
                          pageNumber = index + 1;
                        } else if (currentPage <= 3) {
                          pageNumber = index + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNumber = totalPages - 4 + index;
                        } else {
                          pageNumber = currentPage - 2 + index;
                        }

                        return (
                          <PaginationItem key={pageNumber}>
                            <PaginationLink
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                handlePageChange(pageNumber);
                              }}
                              isActive={currentPage === pageNumber}
                              className="text-black"
                            >
                              {pageNumber}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      })}

                      <PaginationItem>
                        {currentPage < totalPages && (
                          <PaginationNext
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              handlePageChange(currentPage + 1);
                            }}
                            className="text-black"
                          />
                        )}
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ClaimsPage;

const ClaimsSkeleton = () => {
  return (
    <div className="rounded-md border w-full">
      <Table className="w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[120px]">
              <Skeleton className="h-4 w-16" />
            </TableHead>
            <TableHead className="w-[150px]">
              <Skeleton className="h-4 w-24" />
            </TableHead>
            <TableHead className="w-[100px]">
              <Skeleton className="h-4 w-12" />
            </TableHead>
            <TableHead className="w-[120px]">
              <Skeleton className="h-4 w-20" />
            </TableHead>
            <TableHead className="min-w-[200px]">
              <Skeleton className="h-4 w-16" />
            </TableHead>
            <TableHead className="w-[140px]">
              <Skeleton className="h-4 w-16" />
            </TableHead>
            <TableHead className="text-right w-[100px]">
              <Skeleton className="h-4 w-16 ml-auto" />
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 10 }, (_, index) => (
            <TableRow key={index}>
              <TableCell className="w-[120px]">
                <Skeleton className="h-4 w-16" />
              </TableCell>
              <TableCell className="w-[150px]">
                <Skeleton className="h-4 w-20" />
              </TableCell>
              <TableCell className="w-[100px]">
                <Skeleton className="h-6 w-16 rounded-full" />
              </TableCell>
              <TableCell className="w-[120px]">
                <Skeleton className="h-4 w-20" />
              </TableCell>
              <TableCell className="min-w-[200px]">
                <Skeleton className="h-4 w-32" />
              </TableCell>
              <TableCell className="w-[140px]">
                <Skeleton className="h-4 w-24" />
              </TableCell>
              <TableCell className="text-right w-[100px]">
                <div className="flex justify-end space-x-2">
                  <Skeleton className="h-8 w-8 rounded" />
                  <Skeleton className="h-8 w-8 rounded" />
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
